// Utilidades para Schema.org - AMPI Riviera Nayarit
// Genera JSON-LD estructurado para mejorar SEO y rich snippets

// Interface para Property (Propiedad Inmobiliaria)
export interface PropertySchema {
  id: string;
  name: string;
  description: string;
  price: number;
  currency?: string;
  address: {
    street?: string;
    city: string;
    state: string;
    postalCode?: string;
    country: string;
  };
  geo?: {
    latitude: number;
    longitude: number;
  };
  image?: string[];
  numberOfRooms?: number;
  numberOfBedrooms?: number;
  numberOfBathroomsTotal?: number;
  floorSize?: {
    value: number;
    unitCode: string; // "MTK" for square meters
  };
  propertyType?: string; // House, Apartment, Condo, etc.
}

// Schema para Real Estate Listing
export const generateRealEstateSchema = (property: PropertySchema) => {
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "@id": `https://ampirivieranayarit.com/modulob/property/${property.id}`,
    "name": property.name,
    "description": property.description,
    "url": `https://ampirivieranayarit.com/modulob/property/${property.id}`,
    "image": property.image || [],
    "offers": {
      "@type": "Offer",
      "price": property.price,
      "priceCurrency": property.currency || "MXN",
      "availability": "https://schema.org/InStock",
      "url": `https://ampirivieranayarit.com/modulob/property/${property.id}`
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": property.address.street,
      "addressLocality": property.address.city,
      "addressRegion": property.address.state,
      "postalCode": property.address.postalCode,
      "addressCountry": property.address.country
    },
    ...(property.geo && {
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": property.geo.latitude,
        "longitude": property.geo.longitude
      }
    }),
    ...(property.numberOfBedrooms && { "numberOfBedrooms": property.numberOfBedrooms }),
    ...(property.numberOfBathroomsTotal && { "numberOfBathroomsTotal": property.numberOfBathroomsTotal }),
    ...(property.floorSize && {
      "floorSize": {
        "@type": "QuantitativeValue",
        "value": property.floorSize.value,
        "unitCode": property.floorSize.unitCode
      }
    })
  };
};

// Interface para Agent (Asesor)
export interface AgentSchema {
  id: string;
  name: string;
  jobTitle?: string;
  telephone?: string;
  email?: string;
  image?: string;
  description?: string;
}

// Schema para Person (Agente Inmobiliario)
export const generatePersonSchema = (agent: AgentSchema) => {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `https://ampirivieranayarit.com/modulob/agent/${agent.id}`,
    "name": agent.name,
    "jobTitle": agent.jobTitle || "Asesor Inmobiliario",
    "worksFor": {
      "@type": "Organization",
      "name": "AMPI Riviera Nayarit"
    },
    "url": `https://ampirivieranayarit.com/modulob/agent/${agent.id}`,
    ...(agent.image && { "image": agent.image }),
    ...(agent.telephone && { "telephone": agent.telephone }),
    ...(agent.email && { "email": agent.email }),
    ...(agent.description && { "description": agent.description }),
    "memberOf": {
      "@type": "Organization",
      "name": "AMPI - Asociación Mexicana de Profesionales Inmobiliarios"
    }
  };
};

// Interface para Agency (Inmobiliaria)
export interface AgencySchema {
  id: string;
  name: string;
  description?: string;
  telephone?: string;
  email?: string;
  address?: {
    street?: string;
    city: string;
    state: string;
    country: string;
  };
  logo?: string;
}

// Schema para Organization (Inmobiliaria)
export const generateOrganizationSchema = (agency: AgencySchema) => {
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "@id": `https://ampirivieranayarit.com/modulob/agency/${agency.id}`,
    "name": agency.name,
    "url": `https://ampirivieranayarit.com/modulob/agency/${agency.id}`,
    ...(agency.description && { "description": agency.description }),
    ...(agency.logo && { "logo": agency.logo }),
    ...(agency.telephone && { "telephone": agency.telephone }),
    ...(agency.email && { "email": agency.email }),
    ...(agency.address && {
      "address": {
        "@type": "PostalAddress",
        "streetAddress": agency.address.street,
        "addressLocality": agency.address.city,
        "addressRegion": agency.address.state,
        "addressCountry": agency.address.country
      }
    }),
    "memberOf": {
      "@type": "Organization",
      "name": "AMPI Riviera Nayarit"
    }
  };
};

// Schema para BreadcrumbList
export const generateBreadcrumbSchema = (items: Array<{name: string; url: string}>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

// Schema para FAQPage
export interface FAQItem {
  question: string;
  answer: string;
}

export const generateFAQSchema = (faqs: FAQItem[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Función helper para inyectar el schema en el head
export const injectSchema = (schema: any) => {
  if (typeof window === 'undefined') return;

  const scriptId = 'schema-org-json-ld';
  let scriptElement = document.getElementById(scriptId) as HTMLScriptElement | null;

  if (!scriptElement) {
    scriptElement = document.createElement('script') as HTMLScriptElement;
    scriptElement.id = scriptId;
    scriptElement.type = 'application/ld+json';
    document.head.appendChild(scriptElement);
  }

  scriptElement.textContent = JSON.stringify(schema);
};

// Schema para ItemList (listas de propiedades/agentes)
export interface ListItem {
  id: string | number;
  name: string;
  url: string;
  image?: string | string[];
}

export const generateItemListSchema = (
  items: ListItem[],
  listName: string,
  description: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": listName,
    "description": description,
    "numberOfItems": items.length,
    "itemListElement": items.slice(0, 10).map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "url": item.url,
      "name": item.name,
      ...(item.image && { "image": item.image })
    }))
  };
};

// Schema para CollectionPage (páginas de grid/listados)
export const generateCollectionPageSchema = (
  pageName: string,
  description: string,
  path: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": pageName,
    "description": description,
    "url": `https://ampirivieranayarit.com${path}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "AMPI Riviera Nayarit",
      "url": "https://ampirivieranayarit.com"
    }
  };
};

// Schema para WebSite con SearchAction
export const generateWebSiteSchema = (path: string = '/') => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "AMPI Riviera Nayarit",
    "url": `https://ampirivieranayarit.com${path}`,
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://ampirivieranayarit.com/modulob/place-search?query={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };
};

// Schema para AboutPage
export const generateAboutPageSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Acerca de AMPI Riviera Nayarit",
    "description": "Conoce AMPI Riviera Nayarit: nuestra misión, visión y compromiso con la excelencia inmobiliaria",
    "url": "https://ampirivieranayarit.com/modulob/about-us",
    "mainEntity": {
      "@type": "RealEstateOrganization",
      "name": "AMPI Riviera Nayarit",
      "description": "Asociación líder de profesionales inmobiliarios en Riviera Nayarit",
      "url": "https://ampirivieranayarit.com"
    }
  };
};

// Schema para ContactPage
export const generateContactPageSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contacto - AMPI Riviera Nayarit",
    "description": "Contacta a AMPI Riviera Nayarit para dudas, afiliación y servicios inmobiliarios",
    "url": "https://ampirivieranayarit.com/modulob/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "AMPI Riviera Nayarit",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "areaServed": "MX",
        "availableLanguage": ["Spanish", "English"]
      }
    }
  };
};

// Schema para Blog Post
export interface BlogPostSchema {
  id: string;
  title: string;
  description: string;
  author?: string;
  publishedDate?: string;
  modifiedDate?: string;
  image?: string;
}

export const generateBlogPostSchema = (post: BlogPostSchema) => {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.description,
    "image": post.image || "https://ampirivieranayarit.com/wp-content/uploads/2025/03/mob-slide-1.jpg",
    "author": {
      "@type": "Organization",
      "name": post.author || "AMPI Riviera Nayarit"
    },
    "publisher": {
      "@type": "Organization",
      "name": "AMPI Riviera Nayarit",
      "logo": {
        "@type": "ImageObject",
        "url": "https://ampirivieranayarit.com/wp-content/uploads/2025/03/ampi-logo.png"
      }
    },
    "datePublished": post.publishedDate || new Date().toISOString(),
    "dateModified": post.modifiedDate || new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://ampirivieranayarit.com/modulob/blog-detail/${post.id}`
    }
  };
};

// Sprint 11 (F7-009): LocalBusiness para AMPI Riviera Nayarit (boost SEO local).
export const generateLocalBusinessSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://ampirivieranayarit.com/#organization",
    "name": "AMPI Riviera Nayarit",
    "alternateName": "Asociación Mexicana de Profesionales Inmobiliarios — Sección Riviera Nayarit",
    "description": "Asociación profesional de bienes raíces en Bahía de Banderas, Nuevo Vallarta, Sayulita, Punta de Mita y Riviera Nayarit.",
    "url": "https://ampirivieranayarit.com",
    "logo": "https://ampirivieranayarit.com/wp-content/uploads/2025/03/logo-ampi.png",
    "telephone": "+52-322-226-8686",
    "email": "contacto@ampirivieranayarit.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Av. México 1220, Col. Centro",
      "addressLocality": "Nuevo Vallarta",
      "addressRegion": "Nayarit",
      "postalCode": "63735",
      "addressCountry": "MX"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "20.7027",
      "longitude": "-105.2926"
    },
    "areaServed": [
      { "@type": "City", "name": "Nuevo Vallarta" },
      { "@type": "City", "name": "Sayulita" },
      { "@type": "City", "name": "Punta de Mita" },
      { "@type": "City", "name": "Bucerías" },
      { "@type": "AdministrativeArea", "name": "Bahía de Banderas" },
      { "@type": "AdministrativeArea", "name": "Riviera Nayarit" }
    ],
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    "sameAs": [
      "https://www.facebook.com/ampirivieranayarit",
      "https://www.instagram.com/ampirivieranayarit"
    ]
  };
};

export default {
  generateRealEstateSchema,
  generatePersonSchema,
  generateOrganizationSchema,
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateItemListSchema,
  generateCollectionPageSchema,
  generateWebSiteSchema,
  generateAboutPageSchema,
  generateContactPageSchema,
  generateBlogPostSchema,
  generateLocalBusinessSchema,
  injectSchema
};
